import { useState, useEffect, useCallback, useRef } from 'react';

export interface UseTypewriterOptions {
  /** Characters per second. Default: 30 */
  speed?: number;
  /** Delay before starting in ms. Default: 0 */
  delay?: number;
  /** Called when typing completes */
  onComplete?: () => void;
}

export interface UseTypewriterResult {
  /** The currently displayed text */
  displayedText: string;
  /** Whether typing is complete */
  isComplete: boolean;
  /** Skip to end immediately */
  skip: () => void;
  /** Reset and start over */
  reset: () => void;
}

export function useTypewriter(
  text: string,
  options: UseTypewriterOptions = {}
): UseTypewriterResult {
  const { speed = 30, delay = 0, onComplete } = options;

  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const indexRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCompleteRef = useRef(onComplete);

  // Keep onComplete ref up to date
  onCompleteRef.current = onComplete;

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const skip = useCallback(() => {
    clearTimer();
    setDisplayedText(text);
    setIsComplete(true);
    indexRef.current = text.length;
    onCompleteRef.current?.();
  }, [text, clearTimer]);

  const reset = useCallback(() => {
    clearTimer();
    setDisplayedText('');
    setIsComplete(false);
    indexRef.current = 0;
  }, [clearTimer]);

  useEffect(() => {
    // Reset when text changes
    setDisplayedText('');
    setIsComplete(false);
    indexRef.current = 0;
    clearTimer();

    if (text.length === 0) {
      setIsComplete(true);
      onCompleteRef.current?.();
      return;
    }

    const intervalMs = 1000 / speed;

    const typeNextChar = () => {
      if (indexRef.current < text.length) {
        indexRef.current++;
        setDisplayedText(text.slice(0, indexRef.current));

        if (indexRef.current < text.length) {
          timerRef.current = setTimeout(typeNextChar, intervalMs);
        } else {
          setIsComplete(true);
          onCompleteRef.current?.();
        }
      }
    };

    // Start after delay
    timerRef.current = setTimeout(typeNextChar, delay);

    return clearTimer;
  }, [text, speed, delay, clearTimer]);

  return {
    displayedText,
    isComplete,
    skip,
    reset,
  };
}
